import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OrganizationSettings from '../components/settings/OrganizationSettings';
import OrganizationList from '../components/settings/OrganizationList';
import BillingSettings from '../components/settings/BillingSettings';
import SimpleUserManager from '../components/settings/SimpleUserManager';
import EmailTemplateManager from '../components/settings/EmailTemplateManager';
import EmailSettings from '../components/settings/EmailSettings';
import NotificationSettingsPage from './settings/NotificationSettings';
import AISettingsPage from './settings/AISettings';
import CustomFieldsSettings from '../components/settings/CustomFieldsSettings';
import TaskStatusSettings from './settings/TaskStatusSettings';
import AutomationSettings from './settings/AutomationSettings';
import MFAPolicySettings from '../components/settings/MFAPolicySettings';
import { useAuth } from '../contexts/AuthContext';
import { Organization } from '../types/organization';
import { useLanguage } from '../contexts/LanguageContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ServerIcon,
  BellIcon,
  CpuChipIcon,
  Bars3Icon,
  XMarkIcon,
  RectangleStackIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const Settings = () => {
  const { user } = useAuth(); 
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'organizations' | 'users' | 'billing' | 'email-templates' | 'email-settings' | 'notifications' | 'ai-settings' | 'custom-fields' | 'task-statuses' | 'automations' | 'mfa-policy'>('organizations');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const { t } = useLanguage();

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setSelectedOrg({
      ...updatedOrg,
      ownerId: selectedOrg?.ownerId || user?.id || ''
    });
  };

  const tabGroups = [
    {
      id: 'organization',
      label: t('settings.sidebar.groups.organization'),
      tabs: ['organizations', 'users'] as const
    },
    {
      id: 'communication',
      label: t('settings.sidebar.groups.communication'),
      tabs: ['email-settings', 'email-templates', 'notifications'] as const
    },
    {
      id: 'system',
      label: t('settings.sidebar.groups.system'),
      tabs: ['ai-settings', 'custom-fields', 'task-statuses', 'automations', 'billing'] as const
    },
    {
      id: 'security',
      label: t('settings.sidebar.groups.security'),
      tabs: ['mfa-policy'] as const
    }
  ];

  const tabs = [
    {
      id: 'organizations' as const,
      name: t('settings.tabs.organizations.name'),
      icon: BuildingOfficeIcon,
      description: t('settings.tabs.organizations.description'),
      color: 'blue',
      roles: ['admin', 'super_admin', 'user']
    },
    {
      id: 'users' as const,
      name: t('settings.tabs.users.name'),
      icon: UsersIcon,
      description: t('settings.tabs.users.description'),
      color: 'purple',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'notifications' as const,
      name: t('settings.tabs.notifications.name'),
      icon: BellIcon,
      description: t('settings.tabs.notifications.description'),
      color: 'teal',
      roles: ['admin', 'super_admin', 'user']
    },
    {
      id: 'ai-settings' as const,
      name: t('settings.tabs.aiSettings.name'),
      icon: CpuChipIcon,
      description: t('settings.tabs.aiSettings.description'),
      color: 'indigo',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'custom-fields' as const,
      name: t('settings.tabs.customFields.name'),
      icon: RectangleStackIcon,
      description: t('settings.tabs.customFields.description'),
      color: 'emerald',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'task-statuses' as const,
      name: 'Feladat-státuszok',
      icon: RectangleStackIcon,
      description: 'Egyedi feladat-státuszok, színek és sorrend',
      color: 'sky',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'automations' as const,
      name: 'Automatizálások',
      icon: BoltIcon,
      description: '„Ha … akkor …” szabályok: értesítés, feladat létrehozása',
      color: 'amber',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'email-settings' as const,
      name: t('settings.tabs.emailSettings.name'),
      icon: ServerIcon,
      description: t('settings.tabs.emailSettings.description'),
      color: 'green',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'email-templates' as const,
      name: t('settings.tabs.emailTemplates.name'),
      icon: DocumentTextIcon,
      description: t('settings.tabs.emailTemplates.description'),
      color: 'cyan',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'billing' as const,
      name: t('settings.tabs.billing.name'),
      icon: CurrencyDollarIcon,
      description: t('settings.tabs.billing.description'),
      color: 'orange',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'mfa-policy' as const,
      name: t('settings.tabs.mfaPolicy.name'),
      icon: ShieldCheckIcon,
      description: t('settings.tabs.mfaPolicy.description'),
      color: 'red',
      roles: ['super_admin']
    }
  ];

  const colorMap: Record<string, { active: string; bg: string; text: string }> = {
    blue:    { active: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',     bg: 'bg-blue-100 dark:bg-blue-900/20',    text: 'text-blue-600 dark:text-blue-400' },
    purple:  { active: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300', bg: 'bg-purple-100 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400' },
    amber:   { active: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',   bg: 'bg-amber-100 dark:bg-amber-900/20',   text: 'text-amber-600 dark:text-amber-400' },
    sky:     { active: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300',           bg: 'bg-sky-100 dark:bg-sky-900/20',       text: 'text-sky-600 dark:text-sky-400' },
    teal:    { active: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',     bg: 'bg-teal-100 dark:bg-teal-900/20',    text: 'text-teal-600 dark:text-teal-400' },
    green:   { active: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',   bg: 'bg-green-100 dark:bg-green-900/20',  text: 'text-green-600 dark:text-green-400' },
    indigo:  { active: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-100 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400' },
    orange:  { active: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-400' },
    emerald: { active: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-100 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400' },
    red:     { active: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',       bg: 'bg-red-100 dark:bg-red-900/20',     text: 'text-red-600 dark:text-red-400' },
    cyan:    { active: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',     bg: 'bg-cyan-100 dark:bg-cyan-900/20',    text: 'text-cyan-600 dark:text-cyan-400' },
    gray:    { active: 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300',     bg: 'bg-gray-100 dark:bg-gray-700',      text: 'text-gray-600 dark:text-gray-400' }
  };

  const availableTabs = tabs.filter(tab => tab.roles.includes(user?.role || ''));

  const filteredTabs = availableTabs.filter(tab => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return tab.name.toLowerCase().includes(q) || tab.description.toLowerCase().includes(q);
  });

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as typeof activeTab);
    setIsMobileMenuOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col h-screen">
      <CommonPageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        icon={Cog6ToothIcon}
        actionButton={
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        }
      />

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content - Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden pt-4 md:pt-0">
        {/* Sidebar - Navigation */}
        <div className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 transition-transform duration-300 ease-in-out
          fixed md:relative z-50 md:z-auto
          w-64 h-full md:h-auto flex-shrink-0 
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto
        `}>
          <div className="p-3">
            {/* Mobile close button */}
            <div className="md:hidden flex justify-between items-center mb-4 p-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.title')}
              </h3>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Compact header */}
            <div className="mb-4 p-3 bg-primary-600 rounded-lg text-white">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-white/20 rounded-md backdrop-blur-sm">
                  <Cog6ToothIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{t('settings.title')}</h3>
                  <p className="text-xs text-white/70">Szervezet beállítások</p>
                </div>
              </div>
            </div>
            {/* Search */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder={t('settings.sidebar.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-colors"
              />
            </div>

            {/* Grouped Navigation */}
            <nav className="space-y-3">
              {tabGroups.map((group) => {
                const groupTabs = filteredTabs.filter(tab => (group.tabs as readonly string[]).includes(tab.id));
                if (groupTabs.length === 0) return null;
                const isCollapsed = collapsedGroups.has(group.id);

                return (
                  <div key={group.id}>
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <span>{group.label}</span>
                      <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                    </button>

                    {/* Group Tabs */}
                    {!isCollapsed && (
                      <div className="space-y-0.5 mt-1">
                        {groupTabs.map((tab) => {
                          const Icon = tab.icon;
                          const isActive = activeTab === tab.id;
                          const colors = colorMap[tab.color] || colorMap.gray;

                          return (
                            <button
                              key={tab.id}
                              onClick={() => handleTabChange(tab.id)}
                              className={`
                                w-full flex items-center px-2.5 py-2 text-sm font-medium rounded-lg transition-all duration-150 group
                                ${isActive
                                  ? `${colors.active} shadow-sm`
                                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
                                }
                              `}
                            >
                              <div className={`
                                flex items-center justify-center w-7 h-7 rounded-md mr-2.5 transition-all duration-150 flex-shrink-0
                                ${isActive ? colors.bg : 'bg-gray-100 dark:bg-gray-700 group-hover:' + colors.bg}
                              `}>
                                <Icon className={`h-4 w-4 ${isActive ? colors.text : 'text-gray-500 dark:text-gray-400 group-hover:' + colors.text}`} />
                              </div>
                              <div className="text-left flex-1 min-w-0">
                                <div className="font-medium truncate leading-tight">{tab.name}</div>
                                <div className={`text-xs truncate leading-tight ${isActive ? 'opacity-60' : 'text-gray-500 dark:text-gray-400'}`}>
                                  {tab.description}
                                </div>
                              </div>
                              {isActive && (
                                <div className={`w-1 h-5 rounded-full flex-shrink-0 ml-1 ${colors.text.replace('text-', 'bg-')}`} />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 w-full md:w-auto">
          <div className="p-6">

          {/* Organizations */}
          {activeTab === 'organizations' && (
            <OrganizationList />
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <SimpleUserManager onOrganizationUpdate={handleOrganizationUpdate} />
          )}

          {/* Email/SMTP Settings */}
          {activeTab === 'email-settings' && (
            <EmailSettings />
          )}

          {/* Email Templates */}
          {activeTab === 'email-templates' && (
            <EmailTemplateManager />
          )}

          {/* Billing Settings */}
          {activeTab === 'billing' && (
            <BillingSettings />
          )}


          {/* Notifications */}
          {activeTab === 'notifications' && (
            <NotificationSettingsPage embedded={true} />
          )}

          {/* AI Settings */}
          {activeTab === 'ai-settings' && (
            <AISettingsPage />
          )}

          {/* Custom Fields */}
          {activeTab === 'custom-fields' && (
            <CustomFieldsSettings />
          )}

          {activeTab === 'task-statuses' && (
            <TaskStatusSettings />
          )}

          {activeTab === 'automations' && (
            <AutomationSettings />
          )}

          {/* MFA Policy */}
          {activeTab === 'mfa-policy' && (
            <MFAPolicySettings />
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 